import dayjs from "dayjs";
import pt from "dayjs/locale/pt";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import localeData from "dayjs/plugin/localeData";
import minMax from "dayjs/plugin/minMax";

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(minMax);
dayjs.locale({
  ...pt,
  weekStart: 0,
});

export type DateType = Date | string | dayjs.Dayjs;

export enum DateFormats {
  ISO_TIME_DATE = "YYYY-MM-DD 23:59",
  MONTH = "DD [de] MMMM",
  DAY = "MM",
  ISO_DATE = "YYYY-MM-DD",
  ISO_DATE_TIME = "YYYY-MM-DDTHH:mm",
  SMALL_DATE = "DD/MM/YYYY",
  TIMELY_MONTH = "DD [de] MMMM, HH:mm",
  BASIC_DATE = "DD [de] MMMM",
  SMALL_BASIC_DATE = "DD [de] MMM ",
  SMALL_COMPLETE_DATE = "DD [de] MMM, YYYY",
  LONG_DATE = "DD [de] MMMM, YYYY",
  LONG_BRAZILIAN_DATE = "DD [de] MMMM [de] YYYY",
  RANGE_DATE = "YYYY-MM-DD HH:mm:ss",
  SMALL_DATE_TIME = "DD/MM/YYYY HH:mm",
  LONG_DATE_TIME = "D [de] MMMM, YYYY [às] HH:mm",
  SMALL_WEEK_DATE = "ddd, D MMM",
  SMALL_COMPLETE_WEEK_DATE = "dddd, D [de] MMMM [de] YYYY",
  MONTH_YEAR = "MMMM, YYYY",
  MONTH_YEAR_SHORT = "MM/YY",
  YEAR_MONTH_ISO = "YYYY-MM",
  HOURS_MINUTES = "HH:mm",
  HOURS = "HH[h]mm",
}

export function displayDate(date: Date | string, format: string = DateFormats.LONG_DATE): string {
  return dayjs(date).format(format);
}

export function timeInterval(a: Date, b: Date): string {
  return displayTimestamp(Math.abs(a.getTime() - b.getTime()));
}

export function displayTimestamp(timestamp: number): string {
  const seconds = timestamp / 1000;

  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const dDisplay = d > 0 ? d + "d " : "";
  const hDisplay = h > 0 ? h + "h " : "";
  const mDisplay = m > 0 ? m + "m " : "";
  const sDisplay = s > 0 ? s + "s " : "";

  return (dDisplay + hDisplay + mDisplay + sDisplay).trim();
}

export function displayDurationInSeconds(seconds: number) {
  dayjs.extend(duration);
  return dayjs.duration(seconds, "second").format("DD[d] HH[h] mm[m]");
}

export function toHour12(date: Date | string, hour12 = false) {
  return dayjs(date)
    .toDate()
    .toLocaleString("pt-br", {
      hour: "numeric",
      minute: "numeric",
      hour12: hour12,
    })
    .toLowerCase();
}

export function toDate(value: string, format?: string): Date {
  return dayjs(value, format).toDate();
}

export function toISOString(value: DateType): string {
  return dayjs(value).format("YYYY-MM-DD");
}

export function minDate(dates: Date[]): Date {
  return dayjs.min(dates.map((date) => dayjs(date)))?.toDate() ?? dates[0];
}

export function maxDate(dates: Date[]): Date {
  return dayjs.max(dates.map((date) => dayjs(date)))?.toDate() ?? dates[0];
}

export function daysBetween(date1: Date, date2: Date) {
  return dayjs(date1).diff(dayjs(date2), "d");
}

export function getMouthRange(date: Date) {
  return {
    start: dayjs(date).startOf("month").startOf("week"),
    end: dayjs(date).endOf("month").endOf("week"),
  };
}

export function getMonthName(date: Date): string {
  return dayjs(date).format("MMMM");
}

export function getPreviousMonth(date: Date): Date {
  return dayjs(date).subtract(1, "month").toDate();
}

export function getNextMonth(date: Date): Date {
  return dayjs(date).add(1, "month").toDate();
}

export const areSameMonth = (first: Date, second: Date) => {
  return dayjs(first).isSame(dayjs(second), "month");
};

export function getDayAtStart(date: Date): Date {
  return dayjs(date).startOf("day").toDate();
}

export function validateBirthDate(dateString: string): { isValid: boolean; error?: string } {
  if (!validateDate(dateString)) {
    return { isValid: false, error: "Data inválida" };
  }

  const birthDateObj = dayjs(dateString, DateFormats.SMALL_DATE);
  const currentYear = getCurrentYear();
  const birthYear = birthDateObj.year();

  if (birthYear > currentYear) {
    return { isValid: false, error: "A data de nascimento não pode ser futura" };
  }
  if (birthYear < 1900) {
    return { isValid: false, error: "Data de nascimento muito antiga" };
  }
  return { isValid: true };
}

export function getTimeFromNow(date: Date | string): string {
  const pastDate = dayjs(date);
  const nowDate = dayjs();

  const diff = nowDate.diff(pastDate);

  if (diff <= 1000 * 60) return "Há poucos segundos";
  if (diff <= 1000 * 60 * 60) return `Há ${nowDate.diff(pastDate, "m")} minutos`;
  if (diff <= 1000 * 60 * 60 * 24) return `Há ${nowDate.diff(pastDate, "h")} horas`;
  if (diff <= 1000 * 60 * 60 * 24 * 30) return `Há ${nowDate.diff(pastDate, "d")} dias`;
  if (diff <= 1000 * 60 * 60 * 24 * 365) return `Há ${nowDate.diff(pastDate, "M")} meses`;
  return `Há ${nowDate.diff(pastDate, "y")} anos`;
}

export function isValidDate(date?: Date | string): boolean {
  return dayjs(date).isValid();
}

export function getCurrentYear() {
  return dayjs().year();
}

export function getYearsRange(startYear?: number, range = 4) {
  if (!startYear) {
    startYear = getCurrentYear();
  }

  return Array.from({ length: range }, (_, i) => {
    const year = dayjs()
      .year(startYear as number)
      .add(i, "year")
      .year();
    return { label: String(year), value: year };
  });
}

export function getWeekdays(): string[] {
  return dayjs.localeData().weekdays();
}

export function getDaysInMonth(date: DateType): number {
  return dayjs(date).daysInMonth();
}

export function getWeekStartOffset(date: DateType): number {
  const dayjsDate = dayjs(date).startOf("month");

  return dayjsDate.diff(dayjsDate.startOf("week"), "d");
}

export function isDateValid(date: Date) {
  return date instanceof Date && !isNaN(date.getTime());
}

export const isSameDay = (date1: Date | string, date2: Date | string) => {
  return dayjs(date1).isSame(dayjs(date2), "day");
};

export { dayjs };

export function validateDate(value: string) {
  if (typeof value !== "string") {
    return false;
  }

  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return false;
  }

  const partsData = value.split("/");
  const data = {
    day: partsData[0],
    month: partsData[1],
    ano: partsData[2],
  };

  const day = parseInt(data.day);
  const month = parseInt(data.month);
  const ano = parseInt(data.ano);

  const daysMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (ano % 400 === 0 || (ano % 4 === 0 && ano % 100 !== 0)) {
    daysMonth[2] = 29;
  }

  if (month < 1 || month > 12 || day < 1) {
    return false;
  } else if (day > daysMonth[month]) {
    return false;
  }

  return true;
}

/**
 * regular expression to check for valid hour format (01-23)
 */
export function isValidHour(value: string) {
  return /^(0[0-9]|1[0-9]|2[0-3])$/.test(value);
}

/**
 * regular expression to check for valid 12 hour format (01-12)
 */
export function isValid12Hour(value: string) {
  return /^(0[1-9]|1[0-2])$/.test(value);
}

/**
 * regular expression to check for valid minute format (00-59)
 */
export function isValidMinuteOrSecond(value: string) {
  return /^[0-5][0-9]$/.test(value);
}

type GetValidNumberConfig = { max: number; min?: number; loop?: boolean };

export function getValidNumber(
  value: string,
  { max, min = 0, loop = false }: GetValidNumberConfig,
) {
  let numericValue = parseInt(value, 10);

  if (!isNaN(numericValue)) {
    if (!loop) {
      if (numericValue > max) numericValue = max;
      if (numericValue < min) numericValue = min;
    } else {
      if (numericValue > max) numericValue = min;
      if (numericValue < min) numericValue = max;
    }
    return numericValue.toString().padStart(2, "0");
  }

  return "00";
}

export function getValidHour(value: string) {
  if (isValidHour(value)) return value;
  return getValidNumber(value, { max: 23 });
}

export function getValid12Hour(value: string) {
  if (isValid12Hour(value)) return value;
  return getValidNumber(value, { min: 1, max: 12 });
}

export function getValidMinuteOrSecond(value: string) {
  if (isValidMinuteOrSecond(value)) return value;
  return getValidNumber(value, { max: 59 });
}

type GetValidArrowNumberConfig = {
  min: number;
  max: number;
  step: number;
};

export function getValidArrowNumber(value: string, { min, max, step }: GetValidArrowNumberConfig) {
  let numericValue = parseInt(value, 10);
  if (!isNaN(numericValue)) {
    numericValue += step;
    return getValidNumber(String(numericValue), { min, max, loop: true });
  }
  return "00";
}

export function getValidArrowHour(value: string, step: number) {
  return getValidArrowNumber(value, { min: 0, max: 23, step });
}

export function getValidArrow12Hour(value: string, step: number) {
  return getValidArrowNumber(value, { min: 1, max: 12, step });
}

export function getValidArrowMinuteOrSecond(value: string, step: number) {
  return getValidArrowNumber(value, { min: 0, max: 59, step });
}

export function setMinutes(date: Date, value: string) {
  const minutes = getValidMinuteOrSecond(value);
  date.setMinutes(parseInt(minutes, 10));
  return date;
}

export function setSeconds(date: Date, value: string) {
  const seconds = getValidMinuteOrSecond(value);
  date.setSeconds(parseInt(seconds, 10));
  return date;
}

export function setHours(date: Date, value: string) {
  const hours = getValidHour(value);
  date.setHours(parseInt(hours, 10));
  return date;
}

export function set12Hours(date: Date, value: string, period: Period) {
  const hours = parseInt(getValid12Hour(value), 10);
  const convertedHours = convert12HourTo24Hour(hours, period);
  date.setHours(convertedHours);
  return date;
}

export type TimePickerType = "minutes" | "seconds" | "hours" | "12hours";
export type Period = "AM" | "PM";

export function setDateByType(date: Date, value: string, type: TimePickerType, period?: Period) {
  switch (type) {
    case "minutes":
      return setMinutes(date, value);
    case "seconds":
      return setSeconds(date, value);
    case "hours":
      return setHours(date, value);
    case "12hours": {
      if (!period) return date;
      return set12Hours(date, value, period);
    }
    default:
      return date;
  }
}

export function getDateByType(date: Date, type: TimePickerType) {
  switch (type) {
    case "minutes":
      return getValidMinuteOrSecond(String(date.getMinutes()));
    case "seconds":
      return getValidMinuteOrSecond(String(date.getSeconds()));
    case "hours":
      return getValidHour(String(date.getHours()));
    case "12hours": {
      const hours = display12HourValue(date.getHours());
      return getValid12Hour(String(hours));
    }
    default:
      return "00";
  }
}

export function getArrowByType(value: string, step: number, type: TimePickerType) {
  switch (type) {
    case "minutes":
      return getValidArrowMinuteOrSecond(value, step);
    case "seconds":
      return getValidArrowMinuteOrSecond(value, step);
    case "hours":
      return getValidArrowHour(value, step);
    case "12hours":
      return getValidArrow12Hour(value, step);
    default:
      return "00";
  }
}

/**
 * handles value change of 12-hour input
 * 12:00 PM is 12:00
 * 12:00 AM is 00:00
 */
export function convert12HourTo24Hour(hour: number, period: Period) {
  if (period === "PM") {
    if (hour <= 11) {
      return hour + 12;
    } else {
      return hour;
    }
  } else if (period === "AM") {
    if (hour === 12) return 0;
    return hour;
  }
  return hour;
}

/**
 * time is stored in the 24-hour form,
 * but needs to be displayed to the user
 * in its 12-hour representation
 */
export function display12HourValue(hours: number) {
  if (hours === 0 || hours === 12) return "12";
  if (hours >= 22) return `${hours - 12}`;
  if (hours % 12 > 9) return `${hours}`;
  return `0${hours % 12}`;
}
